import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { CarouselSlider, ListItemBox } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { applyStakeholdersSearchCriteria } from "domains/stakeholders/actions";
import { StakeholderTile } from "domains/stakeholders/components/StakeholderTile";
import {
  createStakeholdersForTopicSelector,
  getIsStakeholdersFiltered,
  getStakeholderFilterCount,
} from "domains/stakeholders/selectors";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { getTmSelectedId } from "domains/transformationMap/selectors";
import { useMedia } from "domains/viewport/useMedia";
import { createStakeholdersSelector } from "entities/stakeholders/selectors";

const StyledCarousel = styled(CarouselSlider)`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 5px;
`;

const StyledCard = styled(ListItemBox)``;

type StakeholdersSectionProps = { selectedSectionId?: string; topicId: string };

export const StakeholdersSection = ({ topicId, selectedSectionId }: StakeholdersSectionProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const numberOfItems = useMedia({ small: 2 }, 4);
  const isFiltered = useSelector(getIsStakeholdersFiltered);
  const filterCount = useSelector(getStakeholderFilterCount);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const stakeholdersForTopic = useSelector(createStakeholdersForTopicSelector(id));

  const stakeholdersSelector = useMemo(
    () => createStakeholdersSelector(stakeholdersForTopic?.data ?? []),
    [stakeholdersForTopic?.data],
  );

  const stakeholders = useSelector(stakeholdersSelector);

  const isPending = stakeholdersForTopic?.isFetching ?? false;

  useEffect(() => {
    if (id && stakeholders.length === 0) {
      dispatch(applyStakeholdersSearchCriteria({ offset: 0, limit: 10 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  const path = `${location.pathname}/stakeholders`;

  if (stakeholders.length === 0 && !isPending && !isFiltered) {
    return null;
  }

  return (
    <TopicDetailSection
      display={stakeholders.length !== 0 || !isPending}
      filterCount={filterCount}
      isHidden={!!selectedSectionId}
      isHorizontal
      seeAllLink={path}
      title={translate("app.discover.topic.tabs.stakeholders.title")}
      topicId={topicId}
    >
      <StyledCarousel numberOfItems={numberOfItems} minItemWidth={160}>
        {stakeholders.map((stakeholder) => (
          <StakeholderTile key={stakeholder.id} stakeholder={stakeholder} link={`${path}/${stakeholder.id}`} />
        ))}
      </StyledCarousel>
      {stakeholders.length === 0 && !isPending && isFiltered && (
        <StyledCard>{translate("app.monitor.empty_message_filter")}</StyledCard>
      )}
    </TopicDetailSection>
  );
};
